import React, { useState, useEffect, useRef } from 'react';
import { Package, Globe, Plus, Loader2, AlertCircle, Trash2, ExternalLink, FileText, Upload, CheckCircle2 } from 'lucide-react';
import { API_ROOT } from '../config';

/**
 * Product Management Page
 * Handles adding and listing products from api/product.php
 */
const ProductManagementContent = ({ isDarkMode }) => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [activeTab, setActiveTab] = useState('single');
    const fileInputRef = useRef(null);

    // Single Form states
    const [formData, setFormData] = useState({
        product: '',
        url: ''
    });

    // Bulk Form states
    const [bulkText, setBulkText] = useState('');
    const [bulkResults, setBulkResults] = useState(null);
    const [bulkProgress, setBulkProgress] = useState(null);

    // Fetch products on mount
    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_ROOT}product.php`);
            const data = await response.json();

            if (data.error) {
                setError(data.error);
            } else {
                setProducts(data);
            }
        } catch (err) {
            setError('Failed to load products. Please check your connection.');
            console.error('Fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.product.trim() || !formData.url.trim()) {
            setError('Both product name and URL are required.');
            return;
        }

        setSubmitting(true);
        setError(null);
        setSuccess(null);

        const token = localStorage.getItem('auth_token');

        try {
            const response = await fetch(`${API_ROOT}product.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData),
            });

            const result = await response.json();

            if (result.message === "Berhasil") {
                setSuccess('Product added successfully!');
                setFormData({ product: '', url: '' });
                fetchProducts(); // Refresh list
            } else {
                setError(result.error || 'Failed to add product. Please check your token or input.');
            }
        } catch (err) {
            setError('Network error or server error. Failed to add product.');
            console.error('Submit error:', err);
        } finally {
            setSubmitting(false);
        }
    };

    const handleBulkSubmit = async () => {
        if (!bulkText.trim()) {
            setError('Please enter some products to add.');
            return;
        }

        setSubmitting(true);
        setError(null);
        setSuccess(null);
        setBulkResults(null);
        setBulkProgress(null);

        const lines = bulkText.split('\n').filter(line => line.trim() !== '');
        if (lines.length === 0) {
            setError('No valid lines found.');
            setSubmitting(false);
            return;
        }

        const token = localStorage.getItem('auth_token');
        let successCount = 0;
        let failCount = 0;
        const failedLines = [];
        const totalLines = lines.length;

        setBulkProgress({ current: 0, total: totalLines });

        for (let i = 0; i < lines.length; i++) {
            setBulkProgress({ current: i + 1, total: totalLines });
            const line = lines[i].trim();
            const parts = line.split(';');

            if (parts.length < 2) {
                failCount++;
                failedLines.push(`Line ${i + 1}: Invalid format (missing semicolon)`);
                continue;
            }

            const productName = parts[0].trim();
            const productUrl = parts.slice(1).join(';').trim(); // in case url has semicolon

            if (!productName || !productUrl) {
                failCount++;
                failedLines.push(`Line ${i + 1}: Empty name or URL`);
                continue;
            }

            try {
                const response = await fetch(`${API_ROOT}product.php`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ product: productName, url: productUrl }),
                });

                const result = await response.json();
                if (result.message === "Berhasil") {
                    successCount++;
                } else {
                    failCount++;
                    failedLines.push(`Line ${i + 1}: API Error`);
                }
            } catch (err) {
                failCount++;
                failedLines.push(`Line ${i + 1}: Network Error`);
            }
        }

        setSubmitting(false);
        setBulkProgress(null);
        setBulkResults({ successCount, failCount, failedLines });

        if (successCount > 0) {
            setSuccess(`Successfully added ${successCount} products!`);
            fetchProducts();
            if (failCount === 0) {
                setBulkText(''); // Clear if all successful
            }
        }

        if (failCount > 0) {
            setError(`Failed to add ${failCount} products. Check results below.`);
        }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            setBulkText(prev => prev + (prev ? '\n' : '') + evt.target.result);
        };
        reader.readAsText(file);

        // Reset input so the same file could be selected again
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div className="space-y-6 animate-fade-in max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-600'}`}>
                    <Package size={24} />
                </div>
                <div>
                    <h2 className="text-xl font-bold">Product Management</h2>
                    <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Manage your product listing and external links</p>
                </div>
            </div>

            {/* Success/Error Alerts */}
            {success && (
                <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 px-4 py-3 rounded-lg flex items-center gap-2 text-[10px] transition-all animate-slide-up">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    {success}
                </div>
            )}

            {error && (
                <div className="bg-rose-500/10 border border-rose-500/20 text-rose-500 px-4 py-3 rounded-lg flex items-center gap-2 text-[10px] animate-shake">
                    <AlertCircle size={16} />
                    {error}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Form Section */}
                <div className="lg:col-span-1 border-r pr-6 border-slate-200 dark:border-slate-800">
                    <div className="flex gap-2 mb-6">
                        <button
                            onClick={() => setActiveTab('single')}
                            className={`flex-1 py-2 text-[10px] font-semibold rounded-lg transition-all ${activeTab === 'single' ? 'bg-indigo-500 text-white shadow-md' : 'bg-slate-100 text-slate-500 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'}`}
                        >
                            Single Add
                        </button>
                        <button
                            onClick={() => setActiveTab('bulk')}
                            className={`flex-1 py-2 text-[10px] font-semibold rounded-lg transition-all ${activeTab === 'bulk' ? 'bg-indigo-500 text-white shadow-md' : 'bg-slate-100 text-slate-500 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'}`}
                        >
                            Bulk Add
                        </button>
                    </div>

                    <div className={`p-5 rounded-2xl shadow-xl transition-all border ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`}>
                        <h3 className="text-[10px] font-semibold mb-4 flex items-center gap-2">
                            {activeTab === 'single' ? <Plus size={18} className="text-indigo-500" /> : <FileText size={18} className="text-indigo-500" />}
                            {activeTab === 'single' ? 'Add New Product' : 'Bulk Upload Products'}
                        </h3>

                        {activeTab === 'single' ? (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className={`block text-[10px] font-medium uppercase tracking-wider mb-1.5 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                        Product Name
                                    </label>
                                    <input
                                        type="text"
                                        name="product"
                                        value={formData.product}
                                        onChange={handleInputChange}
                                        placeholder="Enter product title..."
                                        className={`w-full px-4 py-2.5 rounded-xl text-[10px] transition-all outline-none border focus:ring-2 focus:ring-indigo-500/20
                                        ${isDarkMode
                                                ? 'bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-700 focus:border-indigo-500/50'
                                                : 'bg-slate-50 border-slate-200 text-slate-800 placeholder:text-slate-400 focus:border-indigo-400'
                                            }`}
                                    />
                                </div>

                                <div>
                                    <label className={`block text-[10px] font-medium uppercase tracking-wider mb-1.5 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                        Link URL
                                    </label>
                                    <div className="relative">
                                        <Globe size={16} className={`absolute left-4 top-3 ${isDarkMode ? 'text-slate-600' : 'text-slate-400'}`} />
                                        <input
                                            type="url"
                                            name="url"
                                            value={formData.url}
                                            onChange={handleInputChange}
                                            placeholder="https://example.com/product"
                                            className={`w-full pl-11 pr-4 py-2.5 rounded-xl text-[10px] transition-all outline-none border focus:ring-2 focus:ring-indigo-500/20
                                            ${isDarkMode
                                                    ? 'bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-700 focus:border-indigo-500/50'
                                                    : 'bg-slate-50 border-slate-200 text-slate-800 placeholder:text-slate-400 focus:border-indigo-400'
                                                }`}
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className={`w-full py-3 rounded-xl text-[10px] font-bold shadow-lg shadow-indigo-500/10 transition-all flex items-center justify-center gap-2
                                    ${submitting
                                            ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                                            : 'bg-indigo-600 hover:bg-indigo-500 text-white active:scale-[0.98]'
                                        }`}
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 size={18} className="animate-spin" />
                                            Adding...
                                        </>
                                    ) : (
                                        <>
                                            <Plus size={18} />
                                            Add Product
                                        </>
                                    )}
                                </button>
                            </form>
                        ) : (
                            <div className="space-y-4">
                                <div className={`p-3 rounded-lg text-[10px] mb-4 ${isDarkMode ? 'bg-slate-800/50 text-slate-300' : 'bg-blue-50 text-blue-800'}`}>
                                    <p className="font-semibold mb-1">Format Guide:</p>
                                    <code className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-slate-950 text-indigo-400' : 'bg-white text-indigo-600'}`}>product_name;url</code>
                                    <p className="mt-1 opacity-80">One product per line. Example:</p>
                                    <p className="mt-1 font-mono text-[10px] opacity-70">
                                        Kursi Lipat;https://shopee.co.id/xxx<br />
                                        Meja Gaming;https://shopee.co.id/yyy
                                    </p>
                                </div>

                                <textarea
                                    value={bulkText}
                                    onChange={(e) => setBulkText(e.target.value)}
                                    placeholder="Paste your product list here..."
                                    rows="6"
                                    className={`w-full px-4 py-3 rounded-xl text-[10px] transition-all outline-none border focus:ring-2 focus:ring-indigo-500/20 font-mono
                                        ${isDarkMode
                                            ? 'bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-700 focus:border-indigo-500/50'
                                            : 'bg-slate-50 border-slate-200 text-slate-800 placeholder:text-slate-400 focus:border-indigo-400'
                                        }`}
                                />

                                <div className="flex items-center gap-3">
                                    <input
                                        type="file"
                                        accept=".txt"
                                        className="hidden"
                                        ref={fileInputRef}
                                        onChange={handleFileUpload}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        className={`flex-1 py-2.5 rounded-xl text-[10px] font-semibold border-2 border-dashed transition-all flex items-center justify-center gap-2
                                            ${isDarkMode ? 'border-slate-700 hover:border-indigo-500 text-slate-400 hover:text-indigo-400' : 'border-slate-300 hover:border-indigo-500 text-slate-500 hover:text-indigo-600'}`}
                                    >
                                        <Upload size={16} />
                                        Upload .txt File
                                    </button>
                                </div>

                                <button
                                    type="button"
                                    onClick={handleBulkSubmit}
                                    disabled={submitting || !bulkText.trim()}
                                    className={`w-full py-3 rounded-xl text-[10px] font-bold shadow-lg transition-all flex items-center justify-center gap-2
                                        ${submitting || !bulkText.trim()
                                            ? 'bg-slate-700 text-slate-400 cursor-not-allowed opacity-70'
                                            : 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-indigo-500/10 active:scale-[0.98]'
                                        }`}
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 size={18} className="animate-spin" />
                                            {bulkProgress ? `Processing ${bulkProgress.current} of ${bulkProgress.total}...` : 'Processing...'}
                                        </>
                                    ) : (
                                        <>
                                            <Plus size={18} />
                                            Process Bulk Addition
                                        </>
                                    )}
                                </button>

                                {submitting && bulkProgress && (
                                    <div className="w-full bg-slate-200 dark:bg-slate-800 rounded-full h-2 mt-2 overflow-hidden shadow-inner">
                                        <div
                                            className="bg-indigo-500 h-full rounded-full transition-all duration-300 ease-out"
                                            style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }}
                                        />
                                    </div>
                                )}

                                {bulkResults && (
                                    <div className="mt-4 space-y-2">
                                        <div className="flex justify-between text-[10px]">
                                            <span className="text-emerald-500 flex items-center gap-1"><CheckCircle2 size={14} /> {bulkResults.successCount} Success</span>
                                            <span className="text-rose-500 flex items-center gap-1"><AlertCircle size={14} /> {bulkResults.failCount} Failed</span>
                                        </div>
                                        {bulkResults.failedLines.length > 0 && (
                                            <div className={`p-2 rounded mt-2 max-h-32 overflow-y-auto text-[10px] font-mono ${isDarkMode ? 'bg-rose-950/30 text-rose-400' : 'bg-rose-50 text-rose-600'}`}>
                                                {bulkResults.failedLines.map((line, idx) => (
                                                    <div key={idx}>{line}</div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Table Section */}
                <div className="lg:col-span-2">
                    <div className={`rounded-2xl shadow-xl border overflow-hidden transition-all ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`}>
                        <div className={`p-4 border-b flex justify-between items-center ${isDarkMode ? 'border-slate-800 bg-slate-900/50' : 'border-slate-100 bg-slate-50/50'}`}>
                            <h3 className="text-[10px] font-semibold">Existing Products</h3>
                            <button
                                onClick={fetchProducts}
                                className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
                            >
                                <Loader2 size={16} className={loading ? 'animate-spin' : ''} />
                            </button>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead>
                                    <tr className={`text-[10px] uppercase font-bold tracking-wider ${isDarkMode ? 'text-slate-500 bg-slate-950/30' : 'text-slate-400 bg-slate-50'}`}>
                                        <th className="px-6 py-3">#</th>
                                        <th className="px-6 py-3">Product Name</th>
                                        <th className="px-6 py-3">URL</th>
                                        <th className="px-6 py-3 text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-800' : 'divide-slate-100'}`}>
                                    {loading ? (
                                        // Skeleton Rows
                                        [...Array(5)].map((_, i) => (
                                            <tr key={i}>
                                                <td className="px-6 py-4 animate-pulse"><div className={`h-3 w-4 rounded ${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`} /></td>
                                                <td className="px-6 py-4 animate-pulse"><div className={`h-3 w-32 rounded ${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`} /></td>
                                                <td className="px-6 py-4 animate-pulse"><div className={`h-3 w-48 rounded ${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`} /></td>
                                                <td className="px-6 py-4 text-right animate-pulse"><div className={`ml-auto h-3 w-8 rounded ${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`} /></td>
                                            </tr>
                                        ))
                                    ) : products.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="px-6 py-12 text-center">
                                                <div className="flex flex-col items-center opacity-40">
                                                    <Package size={48} className="mb-2" />
                                                    <p className="text-[10px]">No products found</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        products.map((product, idx) => (
                                            <tr key={product.id || idx} className={`text-[10px] group hover:bg-indigo-500/5 transition-colors`}>
                                                <td className={`px-6 py-4 text-[10px] font-mono ${isDarkMode ? 'text-slate-600' : 'text-slate-400'}`}>
                                                    {idx + 1}
                                                </td>
                                                <td className="px-6 py-4 text-[10px]">
                                                    {product.product}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-2 overflow-hidden max-w-[200px]">
                                                        <span className={`truncate text-[10px] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                            {product.url}
                                                        </span>
                                                        <a
                                                            href={product.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-indigo-500 hover:text-indigo-400 transition-colors"
                                                        >
                                                            <ExternalLink size={12} />
                                                        </a>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <button className="text-rose-500 hover:text-rose-400 p-1.5 opacity-0 group-hover:opacity-100 transition-all rounded-lg hover:bg-rose-500/10">
                                                        <Trash2 size={14} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductManagementContent;
